// FLAGS: personal-data
// Form state and the request mapper: carries the employer id and the per-session expert list, and
// is what decides the shape actually submitted to the create endpoint.
/**
 * Pure form state + validation for the Create Private Space flow (OI-24710).
 * Kept free of React so the rules (required fields, >=1 host per session,
 * max people per session, valid capacity) are unit-testable in isolation.
 */
import {
  ContentLanguage,
  CreateSpaceEventRequest,
  ExpertRole,
  SessionExpertInput,
  SpaceEventType,
} from "@src/models/spaces";
import {
  TranslationDraft,
  toEventTranslationPayload,
  toSessionTranslationPayload,
} from "@src/components/spaces/translationDrafts";
import { DEFAULT_TIMEZONE } from "@src/components/spaces/timezones";
import { isValidHttpUrl } from "@src/utils/url";

/** The non-host role for each event type (a masterclass has panelists; a group session, co-hosts). */
export const secondaryRoleFor = (eventType: SpaceEventType): ExpertRole =>
  eventType === "groupsession" ? "cohost" : "panelist";

/** Fixed session lengths offered by the duration picker (minutes). */
export const DURATION_OPTIONS = [30, 45, 60, 90] as const;
export const DEFAULT_DURATION_MINUTES = 60;

/** How a session's length is set: pick a preset duration, or enter an explicit end time. */
export type DurationMode = "duration" | "endTime";

/** Minutes between two "HH:mm" times (same day); negative/zero means end is not after start. */
export const durationFromEndTime = (
  startTime: string,
  endTime: string,
): number => {
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };
  return toMinutes(endTime) - toMinutes(startTime);
};

/**
 * Selecting a company also fills the public display name with that company's
 * name, so the common case needs no typing. A name the scheduler broadened by
 * hand survives a change of company; an untouched prefill follows it.
 */
export const withCompanies = (
  form: EventFormState,
  employers: ReadonlyArray<{ id: string; name: string }>,
  employerIds: string[],
): Partial<EventFormState> => {
  const nameOf = (ids: string[]) =>
    ids.length === 1
      ? (employers.find((employer) => employer.id === ids[0])?.name ?? "")
      : "";

  // A name the scheduler broadened by hand survives a change of companies; an
  // untouched prefill follows it. With several companies there is nothing to
  // prefill from, so the field is left for them to fill in and validation
  // requires it.
  const typedByHand =
    form.employerDisplayName.trim() !== "" &&
    form.employerDisplayName !== nameOf(form.employerIds);

  return {
    employerIds,
    employerDisplayName: typedByHand
      ? form.employerDisplayName
      : nameOf(employerIds),
  };
};

/** Prefilled seat cap; the backend applies the same default when omitted. */
export const DEFAULT_CAPACITY = 100;

/**
 * The language the form starts on. Prefilled rather than blank so a required
 * field is never a trap the scheduler has to discover by failing to submit;
 * most events are authored in English, and changing it is one click.
 */
export const DEFAULT_CONTENT_LANGUAGE: ContentLanguage = "en-GB";

/**
 * Max people per session, host and panelists included. Hosts (Zoom alternative
 * hosts) can start the session; panelists can present but cannot start it.
 */
export const MAX_EXPERTS_PER_SESSION = 3;

export interface SessionFormState {
  /** Stable React key, never sent to the server. */
  key: string;
  /** Optional session name; blank -> backend defaults to the event title. */
  name: string;
  date: string;
  startTime: string;
  /** Whether the length is set by a preset duration or by an explicit end time. */
  durationMode: DurationMode;
  /** One of DURATION_OPTIONS; used when durationMode is "duration". */
  durationMinutes: number;
  /** "HH:mm" end time; used when durationMode is "endTime". */
  endTime: string;
  /** Defaults to Europe/Amsterdam; changing it is optional. */
  timeZone: string;
  /** Raw input; "" falls back to the backend default (100). */
  capacity: string;
  /** Optional practical notes for attendees; blank -> omitted from the request. */
  sessionInstructions: string;
  experts: SessionExpertInput[];
}

export interface EventFormState {
  title: string;
  description: string;
  eventType: SpaceEventType;
  /** Every company the event is open to; at least one is required. */
  employerIds: string[];
  /**
   * Audience name for the public landing page. Prefilled from the chosen company
   * while there is exactly one, and required once there are several: no single
   * company name describes the audience then, and the parent company usually has
   * no record of its own.
   */
  employerDisplayName: string;
  /**
   * Optional third-party survey link attendees are sent to when a session
   * ends. Masterclass-only (Zoom webinar feature); ignored for group sessions.
   */
  surveyUrl: string;
  /**
   * The language the copy above is written in. Required before submitting: it
   * decides which Customer.io template the event's emails go out in, and an
   * event created without one would need a second visit to the edit screen to
   * fix that (OI-25388).
   */
  languageCode: ContentLanguage | "";
  /** One draft per non-source language the scheduler has opened. */
  translations: TranslationDraft[];
  sessions: SessionFormState[];
}

export interface SessionErrors {
  date?: string;
  startTime?: string;
  endTime?: string;
  capacity?: string;
  experts?: string;
}

export interface FormErrors {
  title?: string;
  employerIds?: string;
  employerDisplayName?: string;
  surveyUrl?: string;
  languageCode?: string;
  /** Keyed by session index. */
  sessions: Record<number, SessionErrors>;
}

let keySeq = 0;
export const makeSessionKey = (): string => `session-${keySeq++}`;

export const emptySession = (): SessionFormState => ({
  key: makeSessionKey(),
  name: "",
  date: "",
  startTime: "",
  durationMode: "duration",
  durationMinutes: DEFAULT_DURATION_MINUTES,
  endTime: "",
  timeZone: DEFAULT_TIMEZONE,
  capacity: String(DEFAULT_CAPACITY),
  sessionInstructions: "",
  experts: [],
});

export const emptyForm = (eventType: SpaceEventType): EventFormState => ({
  title: "",
  description: "",
  eventType,
  employerIds: [],
  employerDisplayName: "",
  surveyUrl: "",
  languageCode: DEFAULT_CONTENT_LANGUAGE,
  translations: [],
  sessions: [emptySession()],
});

/** One-line recap of a session ("name · date time · N experts") for collapsed cards. */
export const sessionSummary = (session: SessionFormState): string =>
  [
    session.name.trim(),
    [session.date, session.startTime].filter(Boolean).join(" "),
    session.experts.length > 0 &&
      `${session.experts.length} expert${session.experts.length > 1 ? "s" : ""}`,
  ]
    .filter(Boolean)
    .join(" · ");

export const setExpertRole = (
  experts: SessionExpertInput[],
  userId: string,
  role: ExpertRole | null,
): SessionExpertInput[] => {
  if (!role) {
    return experts.filter((e) => e.userId !== userId);
  }

  // Mapped in place rather than filtered-then-appended: re-appending moved the person to the
  // bottom of the rendered list on every toggle, so the row jumped out from under the cursor —
  // with the remove button sitting right beside the toggle that caused the jump.
  const existing = experts.some((e) => e.userId === userId);
  return existing
    ? experts.map((e) => (e.userId === userId ? { userId, role } : e))
    : [...experts, { userId, role }];
};

const validateSession = (session: SessionFormState): SessionErrors => {
  const errors: SessionErrors = {};
  if (!session.date) {
    errors.date = "Pick a date.";
  }
  if (!session.startTime) {
    errors.startTime = "Pick a start time.";
  }
  if (session.durationMode === "endTime") {
    if (!session.endTime) {
      errors.endTime = "Pick an end time.";
    } else if (
      session.startTime &&
      durationFromEndTime(session.startTime, session.endTime) <= 0
    ) {
      errors.endTime = "End time must be after the start time.";
    }
  }
  if (session.capacity) {
    const capacity = Number(session.capacity);
    if (!Number.isInteger(capacity) || capacity < 1) {
      errors.capacity = "Capacity must be a positive whole number.";
    }
  }
  if (!session.experts.some((e) => e.role === "host")) {
    errors.experts = "Assign at least one host.";
  } else if (session.experts.length > MAX_EXPERTS_PER_SESSION) {
    errors.experts = `A session can have at most ${MAX_EXPERTS_PER_SESSION} people, host included.`;
  }
  return errors;
};

export const validateForm = (form: EventFormState): FormErrors => {
  const errors: FormErrors = { sessions: {} };
  if (!form.title.trim()) {
    errors.title = "Title is required.";
  }
  if (form.employerIds.length === 0) {
    errors.employerIds = "Pick at least one company.";
  }
  // With one company its own name describes the audience; with several, nothing
  // in the system holds the group's name unless the scheduler provides it.
  if (form.employerIds.length > 1 && !form.employerDisplayName.trim()) {
    errors.employerDisplayName =
      "Give the event a public name when it is for more than one company.";
  }
  if (
    form.eventType === "masterclass" &&
    form.surveyUrl.trim() &&
    !isValidHttpUrl(form.surveyUrl)
  ) {
    errors.surveyUrl = "Enter a full link starting with https://";
  }
  if (!form.languageCode) {
    errors.languageCode = "Pick the language this event is written in.";
  }
  form.sessions.forEach((session, index) => {
    const sessionErrors = validateSession(session);
    if (Object.keys(sessionErrors).length > 0) {
      errors.sessions[index] = sessionErrors;
    }
  });
  return errors;
};

export const hasErrors = (errors: FormErrors): boolean =>
  Boolean(errors.title) ||
  Boolean(errors.employerIds) ||
  Boolean(errors.employerDisplayName) ||
  Boolean(errors.surveyUrl) ||
  Boolean(errors.languageCode) ||
  Object.keys(errors.sessions).length > 0;

export const toRequest = (form: EventFormState): CreateSpaceEventRequest => ({
  title: form.title.trim(),
  description: form.description.trim() || null,
  eventType: form.eventType,
  employerIds: form.employerIds,
  employerDisplayName: form.employerDisplayName.trim() || null,
  // The survey is a webinar-only feature — never sent for a group session.
  surveyUrl:
    form.eventType === "masterclass" ? form.surveyUrl.trim() || null : null,
  // Narrowed rather than guarded: validateForm rejects a blank language, and
  // the page will not submit a form with errors.
  languageCode: form.languageCode as ContentLanguage,
  translations: toEventTranslationPayload(form.translations),
  sessions: form.sessions.map((session) => ({
    name: session.name.trim() || null,
    date: session.date,
    startTime: session.startTime,
    durationMinutes:
      session.durationMode === "endTime" && session.startTime && session.endTime
        ? durationFromEndTime(session.startTime, session.endTime)
        : session.durationMinutes,
    timeZone: session.timeZone || null,
    capacity: session.capacity ? Number(session.capacity) : null,
    sessionInstructions: session.sessionInstructions.trim() || null,
    experts: session.experts,
    // The drafts are keyed language -> sessions and the payload wants
    // session -> languages, so each session pulls its own copy back out.
    translations: toSessionTranslationPayload(form.translations, session.key),
  })),
});
